<template>
  <div id="app" @mouseup="handleMouseUp">
    <component v-bind:is="layout"></component>
    <!-- Single shared help dialog for every HelpIcon / navbar Help control. -->
    <help-modal />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import PublicLayout from "./components/PublicLayout";
import PrivateLayout from "./components/PrivateLayout";
import HelpModal from "./components/help/HelpModal.vue";
import { PRIVATE_LAYOUT, PUBLIC_LAYOUT } from "./constants/layout-constants";
import { SESSION_TIME } from "@/config";
import { RESET_TIMER } from "@/constants/event";
import { ping } from "@/api/sessionApi";
import { LogoutRequest } from "@/store/modules/fsm/messages";
import { post } from "@/store/modules/fsm/index";
import EventBus from "./event-bus";

export default {
  name: "App",
  computed: {
    ...mapGetters("login", {
      isLoggedIn: "isLoggedIn",
    }),
    layout() {
      if (this.isLoggedIn) return PRIVATE_LAYOUT;
      else return PUBLIC_LAYOUT;
    },
  },
  components: {
    "public-layout": PublicLayout,
    "private-layout": PrivateLayout,
    HelpModal,
  },
  methods: {
    // Kill browser autofill / saved-value history on every field app-wide.
    // Fields opt back in with data-allow-autocomplete (only the login username).
    enforceNoAutocomplete(root) {
      if (!root || !root.querySelectorAll) return;
      const fields = root.querySelectorAll("input, textarea");
      fields.forEach((el) => {
        if (el.hasAttribute("data-allow-autocomplete")) return;
        if (el.getAttribute("autocomplete") !== "off") {
          el.setAttribute("autocomplete", "off");
        }
      });
    },
    startAutocompletePolicy() {
      this.enforceNoAutocomplete(document);
      this.autocompleteObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          m.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            if (node.matches && node.matches("input, textarea")) {
              if (!node.hasAttribute("data-allow-autocomplete")) {
                node.setAttribute("autocomplete", "off");
              }
            } else {
              this.enforceNoAutocomplete(node);
            }
          });
        }
      });
      this.autocompleteObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    },
    // this tells the server that the client side is active
    handleMouseUp() {
      const isLoggedIn = this.$store.getters["login/isLoggedIn"];
      const isLoggingOut = this.$store.getters["login/isLoggingOut"]; 
      if (isLoggedIn && !isLoggingOut) {
        // Defer slightly to allow logout clicks to set the logging-out flag first
        setTimeout(() => {
          const stillLoggedIn = this.$store.getters["login/isLoggedIn"]; 
          const nowLoggingOut = this.$store.getters["login/isLoggingOut"]; 
          if (!stillLoggedIn || nowLoggingOut) return;
          ping()
            .then(() => {})
            .catch((err) => {
              console.error(err);
            });
        }, 200);
      }
    },
  },
  mounted() {
    console.log('App.vue mounted!');
    this.startAutocompletePolicy();
    // check activity/pulse
    EventBus.on(RESET_TIMER, (info) => {
      // clear timer
      if (this.$sessionTimer) {
        clearTimeout(this.$sessionTimer);
      }
      // set timer
      this.$sessionTimer = setTimeout(() => {
        const isLoggingOut = this.$store.getters["login/isLoggingOut"]; 
        if (this.$store.getters["login/isLoggedIn"] && info.ping && !isLoggingOut) {
          ping()
            .then(() => {
              console.log(`${new Date().toLocaleString()}: pong\n`);
            })
            .catch((err) => {
              // NOTE: timeout is handled in the base request service
              console.error(err);
            });
        }
      }, SESSION_TIME);
    });
  },
  beforeUnmount() {
    clearTimeout(this.$sessionTimer);
    EventBus.off(RESET_TIMER);
    if (this.autocompleteObserver) {
      this.autocompleteObserver.disconnect();
      this.autocompleteObserver = null;
    }
  },
};
</script>

<style>
html {
  position: relative;
  min-height: 100%;
}
#app {
  font-family: "Open Sans", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  /* overflow: hidden; */
}
.page {
  padding-top: 2rem;
  padding-bottom: 8rem;
  /* min-height: 90vh; */
}
.void-filler {
  margin-top: 10%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2em;
}
.container {
  /* max-width: 1000px; */
  max-width: 95vw;
}
.fa-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.file-upload-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-right: 1em;
}
@media only screen and (max-width: 576px) {
  body {
    background-color: #cde4ff !important;
  }
  .page {
    padding-top: 0px;
  }
}

/* Global mobile z-index overrides for mobile message widget */
@media (max-width: 768px) {
  .mobile-message-widget {
    z-index: 9999999999 !important;
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
  }
  
  .mobile-header-bar {
    z-index: 9999999999 !important;
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
  }

  /* AddMedForm (and other body.modal-open modals) must sit above the Messages banner. */
  body.modal-open .mobile-message-widget,
  body.modal-open .mobile-header-bar {
    z-index: 0 !important;
  }
  
  .dashboard {
    z-index: 1 !important;
    position: relative !important;
  }
  
  /* Force all other high z-index elements below mobile widget */
  [class*="Bio"],
  [class*="Nurse"],
  [class*="Provider"],
  [class*="Patient"],
  [class*="Search"],
  [class*="QuickNav"] {
    z-index: 1 !important;
  }
}

/* Desktop floating message widget z-index */
.floating-message-widget {
  z-index: 10000 !important;
}

/* Session timeout and other SweetAlert dialogs must sit above app modals (e.g. note popup). */
.swal2-container {
  z-index: 2147483647 !important;
}
</style>
